import Big from 'big.js';
import { useTranslations } from 'next-intl';
import { OrderUnitType, Instruments, Symbols } from '../types/trade.d';
import { toConvertNumber } from '../func/calculator';

type ConvertQuantityArgs = {
  singleOrderUnitType?: Exclude<OrderUnitType, '2'>;
  singleSymbol?: string;
  supportQuote?: boolean;
  globalSeparator?: boolean;
  priceIncoming?: boolean;
  sOrderUnitType?: OrderUnitType;
  instruments?: Record<string, Instruments> | undefined;
  symbolsKeyby?: Record<string, Symbols>;
};
type ConvertQuantityReturn = Record<'value' | 'reveal' | 'minOrder', string>;

type CalcFnArgs = {
  quantity: string;
  symbol?: string;
  latestPrice?: string;
  separator?: boolean;
};

export const useConvertQuantity: (args?: ConvertQuantityArgs) => (args: CalcFnArgs) => ConvertQuantityReturn = ({
  singleOrderUnitType,
  singleSymbol = '',
  supportQuote = false,
  globalSeparator = true,
  priceIncoming = false,
  sOrderUnitType,
  symbolsKeyby,
  instruments,
} = {}) => {
  const orderUnitType = supportQuote ? sOrderUnitType : sOrderUnitType === '2' ? '0' : sOrderUnitType;

  const t = useTranslations('trade');

  const formulaQuality = {
    '0': ({ quantity, symbol, separator }: CalcFnArgs) => {
      if (symbol || singleSymbol) {
        const value = toConvertNumber(
          new Big(quantity).times(symbolsKeyby?.[symbol || singleSymbol]?.contract_value || 0).toString(),
          {
            precision: symbolsKeyby?.[symbol || singleSymbol]?.contract_digit ?? 2,
            radixPoint: 'SUPPLY_ZERO',
            separator: separator ?? globalSeparator,
          }
        );
        return {
          value,
          reveal: `${value} ${symbolsKeyby?.[symbol || singleSymbol]?.base_currency || ''}`,
          minOrder: new Big(value.replace(/\,/g, ''))
            .times(symbolsKeyby?.[symbol || singleSymbol]?.contract_value_multiplier || 0)
            .toString(),
        };
      }
      return {
        value: '0',
        reveal: `0 ${symbolsKeyby?.[symbol || singleSymbol]?.base_currency || ''}`,
        minOrder: '0',
      };
    },
    '1': ({ quantity }: CalcFnArgs) => {
      return {
        value: quantity,
        reveal: `${quantity} ${t('cont')}`,
        minOrder: new Big(symbolsKeyby?.[singleSymbol]?.contract_value || 0)
          .times(symbolsKeyby?.[singleSymbol]?.contract_value_multiplier || 0)
          .toString(),
      };
    },
    '2': ({ quantity, latestPrice, separator, symbol }: CalcFnArgs) => {
      if (symbol || singleSymbol) {
        const value = toConvertNumber(
          new Big(quantity)
            .times(symbolsKeyby?.[symbol || singleSymbol]?.contract_value || 0)
            .times(latestPrice || instruments?.[symbol || singleSymbol]?.mark_price || 0)
            .toString(),
          { precision: 2, radixPoint: 'SUPPLY_ZERO', separator: separator ?? globalSeparator }
        );

        return {
          value,
          reveal: `${value} ${symbolsKeyby?.[symbol || singleSymbol]?.quote_currency || ''}`,
          minOrder: new Big(value.replace(/\,/g, ''))
            .times(symbolsKeyby?.[symbol || singleSymbol]?.contract_value_multiplier || 0)
            .toString(),
        };
      }
      return {
        value: '0',
        reveal: `0 ${symbolsKeyby?.[symbol || singleSymbol]?.quote_currency || ''}`,
        minOrder: '0',
      };
    },
  };
  return formulaQuality[singleOrderUnitType || orderUnitType || '0'];
};
